"use client";

import { type MotionValue, motion, useTransform } from "motion/react";
import { useId } from "react";
import { cn } from "@/lib/utils";
import { STICKER_ART, STICKER_INK } from "./sticker-art";
import { stickerRimAngle } from "./sticker-display";
import type { WalletSticker } from "./wallet-stickers";

// The drawing sits on a 120 grid; the die-cut edge and its shadow need room
// past it on every side.
const VIEW_MIN = -8;
const VIEW_SIZE = 136;

/** Values the showcase drives every frame; absent everywhere else. */
export interface StickerBlobLive {
  /** px */
  glintX: MotionValue<number>;
  /** px */
  glintY: MotionValue<number>;
  film: MotionValue<number>;
  /** px */
  filmX: MotionValue<number>;
}

/**
 * A wallet sticker as a real die-cut sticker: its own drawing, a white edge
 * that follows the drawing's outline, a soft drop shadow, and a laminate
 * (thin-film sheen and a specular glint) clipped to that silhouette.
 *
 * Static unless `live` is passed. Every live layer is a transform or an
 * opacity, so a moving sticker never rebuilds the drawing.
 */
export function StickerBlob({
  sticker,
  size,
  locked = false,
  live,
  className,
}: {
  sticker: WalletSticker;
  size: number;
  locked?: boolean;
  live?: StickerBlobLive;
  className?: string;
}) {
  // Unique per instance: a gradient or mask id shared between two SVGs stops
  // painting when the defining one is inside a hidden (Activity-parked) route.
  const uid = useId().replace(/:/g, "");
  const maskId = `sticker-mask-${uid}`;
  const filmId = `sticker-film-${uid}`;
  const glintId = `sticker-glint-${uid}`;
  const art = STICKER_ART[sticker.id];
  const unit = size / 100;

  return (
    <span
      aria-hidden
      className={cn(
        "relative inline-block shrink-0",
        // Locked reads as the same sticker, uncoloured and quiet — still the
        // shape you are working towards.
        locked && "opacity-55 contrast-[0.9] grayscale",
        className,
      )}
      style={{ width: size, height: size }}
    >
      <svg
        aria-hidden
        viewBox={`${VIEW_MIN} ${VIEW_MIN} ${VIEW_SIZE} ${VIEW_SIZE}`}
        width={size}
        height={size}
        className="overflow-visible"
        style={{
          filter: `drop-shadow(0 ${2 * unit}px ${3 * unit}px rgba(31,22,64,0.24))`,
        }}
      >
        <defs>
          <mask
            id={maskId}
            maskUnits="userSpaceOnUse"
            x={VIEW_MIN}
            y={VIEW_MIN}
            width={VIEW_SIZE}
            height={VIEW_SIZE}
          >
            <g className="sticker-cut">{art}</g>
          </mask>
          {/* Thin-film interference: the pastel sheen a holographic laminate
              throws back, as one band that slides across the face. */}
          <linearGradient id={filmId} x1="0" y1="0" x2="1" y2="0.45">
            <stop offset="0.18" stopColor="#ffaadc" stopOpacity="0" />
            <stop offset="0.34" stopColor="#ffaadc" stopOpacity="0.85" />
            <stop offset="0.46" stopColor="#cdb4ff" stopOpacity="0.85" />
            <stop offset="0.58" stopColor="#96e1ff" stopOpacity="0.85" />
            <stop offset="0.68" stopColor="#aaffe1" stopOpacity="0.6" />
            <stop offset="0.82" stopColor="#aaffe1" stopOpacity="0" />
          </linearGradient>
          <radialGradient id={glintId}>
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
            <stop offset="0.55" stopColor="#ffffff" stopOpacity="0.3" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
        </defs>

        <g className="sticker-cut">{art}</g>
        <g
          stroke={STICKER_INK}
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {art}
        </g>

        <g mask={`url(#${maskId})`}>
          {live ? (
            <LiveLaminate
              live={live}
              size={size}
              angle={stickerRimAngle(sticker.id)}
              filmId={filmId}
              glintId={glintId}
            />
          ) : (
            <g transform={`rotate(${stickerRimAngle(sticker.id)} 60 60)`}>
              <rect
                x={-40}
                y={-40}
                width={200}
                height={200}
                fill={`url(#${filmId})`}
                opacity={0.14}
              />
              <ellipse
                cx={38}
                cy={24}
                rx={24}
                ry={13}
                fill={`url(#${glintId})`}
                transform="rotate(-28 38 24)"
              />
            </g>
          )}
        </g>
      </svg>
    </span>
  );
}

/** The showcase's moving laminate: pose-driven px turned into drawing units. */
function LiveLaminate({
  live,
  size,
  angle,
  filmId,
  glintId,
}: {
  live: StickerBlobLive;
  size: number;
  angle: number;
  filmId: string;
  glintId: string;
}) {
  const scale = VIEW_SIZE / size;
  const glintX = useTransform(live.glintX, (px) => px * scale);
  const glintY = useTransform(live.glintY, (px) => px * scale);
  const filmX = useTransform(live.filmX, (px) => px * scale);

  return (
    <>
      <g transform={`rotate(${angle} 60 60)`}>
        <motion.rect
          x={-40}
          y={-40}
          width={200}
          height={200}
          fill={`url(#${filmId})`}
          style={{ x: filmX, opacity: live.film }}
        />
      </g>
      <motion.g style={{ x: glintX, y: glintY }}>
        <ellipse
          cx={38}
          cy={24}
          rx={24}
          ry={13}
          fill={`url(#${glintId})`}
          transform="rotate(-28 38 24)"
        />
      </motion.g>
    </>
  );
}
